import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import router from '@/router/index'

// Notification

export default function useLotList() {
  // Use toast
  const toast = useToast()
  const route = router.history.current

  const refLotsTable = ref(null)

  // Table Handlers
  const tableColumns = [
    {
      key: 'lotNo',
      label: 'Lot No.',
      sortable: true,
    },
    {
      key: 'status',
      label: 'Lot Status',
      sortable: true,
    },
    {
      key: 'productType',
      label: 'Product Type',
      sortable: false,
    },
    {
      key: 'lotType',
      label: 'Lot Type',
      sortable: true,
    },
    {
      key: 'createdBy',
      label: 'Created by',
      sortable: true,
    },
    {
      key: 'updatedBy',
      label: 'Last Updated by',
      sortable: false,
    },
    { key: 'actions' },
  ]
  const perPage = ref(route.query.perPage ?? process.env.VUE_APP_PER_PAGE_CONTENT)
  const totalLots = ref(0)
  const currentPage = ref(route.query.page ?? 1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref(route.query.search ?? '')
  const sortBy = ref(route.query.sortBy ?? 'order')
  const isSortDirDesc = ref(route.query.sortDesc ? (route.query.sortDesc === 'true') : false)
  const suiteFilter = ref(route.params.id || null)
  const productFilter = ref([])
  if (Array.isArray(route.query.product)) {
    productFilter.value = route.query.product
  } else if (route.query.product) {
    productFilter.value = [route.query.product]
  }
  const statusFilter = ref([])
  if (Array.isArray(route.query.status)) {
    statusFilter.value = route.query.status
  } else if (route.query.status) {
    statusFilter.value = [route.query.status]
  }
  const lotTypeFilter = ref([])
  if (Array.isArray(route.query.reservable)) {
    lotTypeFilter.value = route.query.reservable
  } else if (route.query.reservable) {
    lotTypeFilter.value = [route.query.reservable]
  }
  const gisMap = ref([])
  if (Array.isArray(route.query.gisMap)) {
    gisMap.value = route.query.gisMap.map(o => Number(o))
  } else if (route.query.gisMap) {
    gisMap.value = [Number(route.query.gisMap)]
  }
  const toDeletedID = ref(null)
  const currentDataLength = ref(0)
  const itemListData = ref([])
  let initialLoad = true

  const dataMeta = computed(() => {
    const localItemsCount = refLotsTable.value ? refLotsTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalLots.value,
    }
  })

  const refetchData = () => {
    refLotsTable.value.refresh()
  }

  // watch([searchQuery, idSearchQuery, statusFilter, createdAtFilter], () => {
  //   currentPage.value = 1
  //   refetchData()
  // }, { deep: true })

  // watch([currentPage], () => {
  //   refetchData()
  // }, { deep: true })

  const fetchLots = () => {
    store
      .dispatch('app-lots/fetchLots', {
        search: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        product: productFilter.value,
        status: statusFilter.value,
        lotType: lotTypeFilter.value,
        gisMap: gisMap.value,
        suite: suiteFilter.value,
      },
      { root: true })
      .then(response => {
        const { lots, total } = response.data
        itemListData.value = lots
        currentDataLength.value = lots.length
        // callback(locations)
        totalLots.value = total
        if (!initialLoad) {
          router.replace({
            query: {
              search: searchQuery.value,
              perPage: perPage.value,
              page: currentPage.value,
              sortBy: sortBy.value,
              sortDesc: isSortDirDesc.value,
              product: productFilter.value,
              status: statusFilter.value,
              lotType: lotTypeFilter.value,
              gisMap: gisMap.value,
            },
          }).catch(() => {})
        }
        initialLoad = false
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  let timer = null
  watch([productFilter, statusFilter, lotTypeFilter, gisMap], () => {
    if (timer) {
      clearTimeout(timer)
    }
    timer = setTimeout(() => {
      currentPage.value = 1
      fetchLots()
    }, 1000)
  }, { deep: true })

  watch([searchQuery], (current, prev) => {
    const currentVal = current[0]
    const prevVal = prev[0]
    if (timer) {
      clearTimeout(timer)
    }
    timer = setTimeout(() => {
      if ((!currentVal && prevVal) || (currentVal && currentVal.length > 2)) {
        currentPage.value = 1
        fetchLots()
      }
    }, 1000)
  }, { deep: true })

  watch([sortBy, perPage, isSortDirDesc, currentPage], () => {
    fetchLots()
  }, { deep: true })

  fetchLots()

  const deleteLot = () => {
    store
      .dispatch('app-lots/deleteLot', {
        id: toDeletedID.value,
      },
      { root: true })
      .then(response => {
        fetchLots()
        toast({
          component: ToastificationContent,
          props: {
            title: response.data.message ?? '',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  // const resolveStatusVariant = status => {
  //   if (status === 'OPEN') return 'primary'
  //   if (status === 'RESERVE') return 'warning'
  //   if (status === 'BOOK') return 'danger'
  //   if (status === 'CONFIRM') return 'success'
  //   if (status === 'HOLD') return 'info'
  //   return 'primary'
  // }

  const resolveStatusVariant = status => {
    if (status === 'Available') return 'primary'
    if (status === 'Reserved') return 'warning'
    if (status === 'Booked') return 'danger'
    if (status === 'Confirmed') return 'success'
    if (status === 'Occupied') return 'info'
    return 'primary'
  }

  return {
    fetchLots,
    tableColumns,
    perPage,
    currentPage,
    totalLots,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refLotsTable,

    refetchData,

    toDeletedID,
    deleteLot,
    currentDataLength,
    itemListData,
    resolveStatusVariant,

    // Extra Filters
    productFilter,
    statusFilter,
    lotTypeFilter,
    gisMap,
  }
}
